import { Injectable, inject, signal } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';

/**
 * Auth0 error codes meaning the user has to sign in again: silent token
 * renewal failed because the session expired, or needs user interaction.
 */
const SESSION_EXPIRED_ERRORS = new Set([
  'login_required',
  'consent_required',
  'interaction_required',
  'missing_refresh_token',
  'invalid_grant'
]);

/**
 * Ends the local session when authentication lapses: clears locally stored
 * work (so a stale form or workflow isn't reopened after signing back in)
 * and sends the user to the login page.
 */
@Injectable({ providedIn: 'root' })
export class SessionTimeoutService {
  private readonly auth = inject(AuthService);

  private readonly redirecting = signal(false);
  /**
   * True once a session-expiry redirect has started. Editors check this to
   * skip their "unsaved changes" prompt, which would otherwise block it.
   */
  readonly isRedirecting = this.redirecting.asReadonly();

  isSessionExpiredError(error: unknown): boolean {
    const code = (error as { error?: string } | null)?.error;
    return !!code && SESSION_EXPIRED_ERRORS.has(code);
  }

  /** Clear local data and redirect to login. Safe to call repeatedly. */
  handleSessionExpired(): void {
    if (this.redirecting()) return;
    this.redirecting.set(true);

    this.clearLocalSessionData();
    // Land on the home page after signing in, not back in an editor.
    this.auth.loginWithRedirect({ appState: { target: '/' } });
  }

  /**
   * Remove everything the app keeps in localStorage: form and workflow
   * drafts, recovery markers, UI preferences and Auth0's (by now invalid)
   * token cache.
   */
  clearLocalSessionData(): void {
    try {
      localStorage.clear();
    } catch {
      // Storage unavailable (e.g. blocked by the browser): nothing to clear.
    }
  }
}
